import React, { useState, useEffect } from 'react'
import { Search } from 'lucide-react'
import helpData from '../../src/data/content/helpData.json'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

interface FAQItem {
  id: string
  question: string
  answer: string
}

interface FAQSection {
  id: string
  title: string
  items: FAQItem[]
}

const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
}

const HelpPage: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  const faqSections: FAQSection[] = helpData.sections.map(section => ({
    id: section.id,
    title: t(section.titleKey),
    items: section.items.map(item => ({
      id: item.id,
      question: t(item.questionKey),
      answer: t(item.answerKey),
    })),
  }))

  const renderAnswer = (answer: string) => {
    // Recherche le texte "Centres d'aides" et le remplace par un lien
    const parts = answer.split(/(Centres d'aides)/g)
    return parts.map((part, index) => {
      if (part === "Centres d'aides") {
        return (
          <Link key={index} to='/asbl' className='text-redpink hover:underline'>
            {part}
          </Link>
        )
      }
      return part
    })
  }

  const [filteredSections, setFilteredSections] = useState(faqSections)

  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm)
    }
  }, [location])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSections(faqSections)
      return
    }

    const normalizedSearchTerm = normalizeString(searchTerm)
    const filtered = faqSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          const normalizedQuestion = normalizeString(item.question)
          const normalizedAnswer = normalizeString(item.answer)
          return (
            normalizedQuestion.includes(normalizedSearchTerm) ||
            normalizedAnswer.includes(normalizedSearchTerm)
          )
        }),
      }))
      .filter(section => section.items.length > 0)

    setFilteredSections(filtered)
  }, [searchTerm])

  const handleItemClick = (itemId: string) => {
    setActiveItemId(activeItemId === itemId ? null : itemId)
  }

  return (
    <>
      <div className='bg-redpink text-white  text-center text-lg md:text-xl lg:text-2xl py-3 mb-0'>
        <p className='uppercase'>{t('help-title')}</p>
        <span className='text-sm md:text-base lg:text-base'>
          {t('help-description')}
        </span>
      </div>

      <article className='mx-auto max-w-7xl px-2 lg:px-0 my-2 lg:my-8'>
        <section className='mb-8'>
          <div className='relative'>
            <input
              type='search'
              placeholder={t('search')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='w-full p-4 pl-12 rounded-lg border border-gray-300 outline-none hover:bg-grisclair'
            />
            <Search className='h-6 w-6 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
          </div>

          {searchTerm.trim() !== '' && (
            <div className='mt-4 text-redpink'>
              {filteredSections.reduce(
                (total, section) => total + section.items.length,
                0,
              )}{' '}
              {t('search-results-found')}
            </div>
          )}
        </section>

        <div className='space-y-12'>
          {filteredSections.map(section => (
            <section key={section.id} id={section.id} className='scroll-mt-20'>
              <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-6'>
                {section.title}
              </h2>
              <div className='space-y-4'>
                {section.items.map(item => (
                  <div
                    key={item.id}
                    className='bg-grisclair rounded-lg overflow-hidden'
                  >
                    <button
                      onClick={() => handleItemClick(item.id)}
                      className='w-full text-left p-4 flex justify-between items-center bg-grisclair2 hover:bg-grisclair2/30 transition-all'
                    >
                      <span className='font-medium text-anthracite'>
                        {item.question}
                      </span>
                      <span className='ml-4 flex-shrink-0 text-lg'>
                        {activeItemId === item.id ? '−' : '+'}
                      </span>
                    </button>
                    {activeItemId === item.id && (
                      <div className='p-4  text-gray-600'>
                        {renderAnswer(item.answer)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {filteredSections.length === 0 && searchTerm.trim() !== '' && (
          <div className='text-center text-anthracite bg-grisclair rounded-lg p-8'>
            {t('no-result-found')}
          </div>
        )}
      </article>
    </>
  )
}

export default HelpPage
